import API from '../../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Card, Row, Col } from 'react-bootstrap';

import { Link } from 'react-router-dom';
const MAX_RATING = 5;

/* Podium treatment for the top three; everyone else gets the neutral chip. */
const PODIUM = [
  {
    medal: '🥇',
    color: '#b45309',
    ring: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
    soft: '#fffbeb',
    border: '#fde68a'
  },
  {
    medal: '🥈',
    color: '#57534e',
    ring: 'linear-gradient(135deg, #e7e5e4, #a8a29e)',
    soft: '#fafaf9',
    border: '#e7e5e4'
  },
  {
    medal: '🥉',
    color: '#9a3412',
    ring: 'linear-gradient(135deg, #fdba74, #c2410c)',
    soft: '#fff7ed',
    border: '#fed7aa'
  }
];

const NEUTRAL = {
  medal: null,
  color: '#78716c',
  ring: 'linear-gradient(135deg, #f5f5f4, #e7e5e4)',
  soft: '#fdfcfa',
  border: '#f3efe8'
};

const rankStyle = (index) => PODIUM[index] || NEUTRAL;

/* Five stars filled to the real average. The number is always written out
   beside them, so rating never rests on the star shapes alone. */
function Stars({ average }) {
  const filled = Math.round(average);

  return (
    <span className="pr-stars" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= filled ? 'on' : 'off'}>
          ★
        </span>
      ))}
    </span>
  );
}

/* Shared leaderboard shell */
function Board({
  icon,
  title,
  hint,
  accent,
  loading,
  items,
  emptyIcon,
  emptyText,
  to,
  toLabel,
  renderRow
}) {
  return (
    <Card className="border-0 h-100 pr-card" style={{ '--accent': accent }}>
      <span className="pr-card-top" />

      <Card.Body className="p-3 p-md-4 d-flex flex-column">
        <div className="d-flex align-items-center gap-3 mb-1">
          <span className="pr-icon">{icon}</span>

          <div className="min-w-0">
            <h5 className="pr-title">{title}</h5>
            <small className="pr-hint">{hint}</small>
          </div>
        </div>

        <div className="pr-rule" />

        {loading ? (
          <div className="d-flex flex-column gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div className="pr-skel-row" key={i}>
                <span className="pr-skel pr-skel-rank" />
                <div className="flex-grow-1">
                  <span className="pr-skel pr-skel-line" />
                  <span className="pr-skel pr-skel-line short" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="pr-empty">
            <div className="pr-empty-icon">{emptyIcon}</div>
            <small className="text-muted">{emptyText}</small>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {items.map((item, index) => renderRow(item, index))}
          </div>
        )}

        {to && !loading && (
          <Link to={to} className="pr-all mt-auto pt-3">
            {toLabel} →
          </Link>
        )}
      </Card.Body>
    </Card>
  );
}

/* Top rated and best selling product leaderboards. */
function ProductRankings() {
  const [topRatedProducts, setTopRatedProducts] = useState([]);
  const [bestSellingProducts, setBestSellingProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async (url, setter) => {
      try {
        const { data } = await axios.get(url);
        setter(data);
      } catch (error) {
        console.log(error);
      }
    };

    Promise.all([
      load(`${API}/admin/top-rated-products`, setTopRatedProducts),
      load(`${API}/admin/best-selling-products`, setBestSellingProducts)
    ]).finally(() => setLoading(false));
  }, []);

  /* The API returns averageRating from toFixed(), i.e. a string */
  const rated = topRatedProducts.map((product) => ({
    ...product,
    rating: Number(product.averageRating) || 0
  }));

  const totalReviews = rated.reduce(
    (sum, product) => sum + (product.totalReviews || 0),
    0
  );

  /* Bars are drawn relative to the leader, so #1 is always a full bar */
  const topSold = bestSellingProducts.reduce(
    (max, product) => Math.max(max, product.totalSold || 0),
    0
  );

  const totalSold = bestSellingProducts.reduce(
    (sum, product) => sum + (product.totalSold || 0),
    0
  );

  return (
    <>
      <style>{productRankingStyles}</style>

      <Row className="g-4 mb-5">
        {/* ===== Top rated ===== */}
        <Col lg={6}>
          <Board
            icon="⭐"
            title="Top Rated Products"
            hint={
              totalReviews > 0
                ? `Across ${totalReviews} customer review${
                    totalReviews === 1 ? '' : 's'
                  }`
                : 'Ranked by average customer rating'
            }
            accent="#f59e0b"
            loading={loading}
            items={rated}
            emptyIcon="📝"
            emptyText="No reviews yet — ratings will rank here once customers start reviewing."
            to="/admin/reviews"
            toLabel="Manage reviews"
            renderRow={(product, index) => {
              const style = rankStyle(index);

              const pct = (product.rating / MAX_RATING) * 100;

              return (
                <div
                  className="pr-row"
                  key={`${product.name}-${index}`}
                  style={{
                    '--rank': style.color,
                    '--rank-ring': style.ring,
                    '--rank-soft': style.soft,
                    '--rank-border': style.border
                  }}
                >
                  <span className="pr-rank">
                    {style.medal || index + 1}
                  </span>

                  <div className="pr-info">
                    <div className="pr-name" title={product.name}>
                      {product.name}
                    </div>

                    <div className="pr-meta">
                      <Stars average={product.rating} />

                      <span className="pr-rating-num">
                        {product.rating.toFixed(1)}
                      </span>

                      <span className="pr-sub">
                        {product.totalReviews} review
                        {product.totalReviews === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="pr-bar">
                      <span
                        className="pr-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </Col>

        {/* ===== Best selling ===== */}
        <Col lg={6}>
          <Board
            icon="🔥"
            title="Best Selling Products"
            hint={
              totalSold > 0
                ? `${totalSold.toLocaleString('en-IN')} units across the top 5`
                : 'Ranked by units sold'
            }
            accent="#16a34a"
            loading={loading}
            items={bestSellingProducts}
            emptyIcon="📦"
            emptyText="No sales yet — your bestsellers will appear here."
            to="/admin/products"
            toLabel="Manage products"
            renderRow={(product, index) => {
              const style = rankStyle(index);

              const pct = topSold
                ? ((product.totalSold || 0) / topSold) * 100
                : 0;

              const share = totalSold
                ? ((product.totalSold || 0) / totalSold) * 100
                : 0;

              return (
                <div
                  className="pr-row pr-row-sell"
                  key={`${product.name}-${index}`}
                  style={{
                    '--rank': style.color,
                    '--rank-ring': style.ring,
                    '--rank-soft': style.soft,
                    '--rank-border': style.border
                  }}
                >
                  <span className="pr-rank">
                    {style.medal || index + 1}
                  </span>

                  <div className="pr-info">
                    <div className="pr-name" title={product.name}>
                      {product.name}
                    </div>

                    <div className="pr-meta">
                      <span className="pr-units">
                        {Number(product.totalSold || 0).toLocaleString('en-IN')}{' '}
                        units
                      </span>

                      <span className="pr-sub">
                        {share.toFixed(0)}% of top 5
                      </span>
                    </div>

                    <div className="pr-bar">
                      <span
                        className="pr-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </Col>
      </Row>
    </>
  );
}

const productRankingStyles = `
  .pr-card {
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 8px 24px rgba(124, 45, 18, 0.08);
    transition: box-shadow 0.25s ease;
  }
  .pr-card:hover {
    box-shadow: 0 16px 34px rgba(124, 45, 18, 0.13);
  }
  /* Accent strip identifies the board */
  .pr-card-top {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--accent);
  }

  .pr-icon {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    border-radius: 14px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .pr-title {
    font-weight: 800;
    margin: 0;
    font-size: 1.05rem;
    color: #7c2d12;
  }
  .pr-hint {
    color: #a8a29e;
    font-size: 0.76rem;
  }
  .pr-rule {
    height: 1px;
    background: linear-gradient(90deg, #fed7aa, transparent);
    margin: 14px 0;
  }
  .min-w-0 { min-width: 0; }

  /* Leaderboard rows */
  .pr-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 14px;
    background: var(--rank-soft);
    border: 1px solid var(--rank-border);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .pr-row:hover {
    transform: translateX(3px);
    box-shadow: 0 8px 20px rgba(124, 45, 18, 0.12);
  }

  /* Rank medal / number */
  .pr-rank {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 1.15rem;
    font-weight: 800;
    color: var(--rank);
    background: var(--rank-ring);
    box-shadow: inset 0 0 0 2px #fff;
  }

  .pr-info { flex: 1 1 auto; min-width: 0; }
  .pr-name {
    font-weight: 700;
    font-size: 0.92rem;
    color: #1c1917;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .pr-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 7px;
    margin: 3px 0 6px;
  }
  .pr-sub {
    font-size: 0.72rem;
    color: #a8a29e;
    font-weight: 600;
  }

  /* Stars */
  .pr-stars {
    font-size: 0.78rem;
    letter-spacing: 1px;
    line-height: 1;
    white-space: nowrap;
  }
  .pr-stars .on  { color: #f59e0b; }
  .pr-stars .off { color: #d8d3cb; }
  .pr-rating-num {
    font-weight: 800;
    font-size: 0.8rem;
    color: #b45309;
    font-variant-numeric: tabular-nums;
  }
  .pr-units {
    font-weight: 800;
    font-size: 0.8rem;
    color: #15803d;
    font-variant-numeric: tabular-nums;
  }

  /* Performance bar */
  .pr-bar {
    height: 6px;
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.08);
    overflow: hidden;
  }
  .pr-bar-fill {
    display: block;
    height: 100%;
    border-radius: 6px;
    background: linear-gradient(90deg, #fbbf24, #f59e0b);
    transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .pr-row-sell .pr-bar-fill {
    background: linear-gradient(90deg, #22c55e, #16a34a);
  }

  .pr-all {
    font-size: 0.8rem;
    font-weight: 700;
    color: #ea580c;
    text-decoration: none;
    align-self: flex-start;
  }
  .pr-all:hover { color: #c2410c; text-decoration: underline; }

  /* Empty + loading */
  .pr-empty {
    text-align: center;
    padding: 34px 12px;
  }
  .pr-empty-icon {
    font-size: 2.4rem;
    margin-bottom: 6px;
  }
  .pr-skel-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid #f3efe8;
    background: #fdfcfa;
  }
  .pr-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: prShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .pr-skel-rank { width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0; }
  .pr-skel-line { height: 11px; width: 70%; margin-bottom: 8px; }
  .pr-skel-line.short { width: 45%; margin-bottom: 0; }
  @keyframes prShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (max-width: 575.98px) {
    .pr-icon { width: 36px; height: 36px; font-size: 1rem; }
    .pr-title { font-size: 0.95rem; }
    .pr-rank { width: 32px; height: 32px; font-size: 1rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .pr-card, .pr-row, .pr-bar-fill, .pr-skel {
      transition: none;
      animation: none;
    }
  }
`;

export default ProductRankings;
